import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["open", "onOpen", "onClose", "mountNode", "insertAt", "transition", "onEnter", "onEntering", "onEntered", "onExit", "onExiting", "onExited", "constrain", "overflow"];
var _dec, _dec2, _class, _Modal;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { Children, Component, isValidElement } from 'react';
import { passthroughProps, safeCloneElement } from '@instructure/ui-react-utils';
import { createChainedFunction } from '@instructure/ui-utils';
import { testable } from '@instructure/ui-testable';
import { Transition } from '@instructure/ui-motion';
import { Portal } from '@instructure/ui-portal';
import { Dialog } from '@instructure/ui-dialog';
import { Mask } from '@instructure/ui-overlays';
import { ModalHeader } from './ModalHeader';
import { ModalBody } from './ModalBody';
import { ModalFooter } from './ModalFooter';
import { withStyle } from '@instructure/emotion';
import generateStyle from './styles';
import generateComponentTheme from './theme';
import { propTypes, allowedProps } from './props';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
/**
---
category: components
tags: overlay, portal, dialog
---
**/
let Modal = (_dec = withStyle(generateStyle, generateComponentTheme), _dec2 = testable(), _dec(_class = _dec2(_class = (_Modal = class Modal extends Component {
  constructor(props) {
    var _props$open;
    super(props);
    this._DOMNode = null;
    this._content = null;
    this.ref = null;
    this.handleRef = el => {
      this.ref = el;
    };
    this.updateHeight = () => {
      this.setState({
        windowHeight: window.innerHeight
      });
    };
    this.handlePortalOpen = DOMNode => {
      this.DOMNode = DOMNode;
    };
    this.handleTransitionComplete = () => {
      this.setState({
        transitioning: false
      });
    };
    this.contentRef = el => {
      this._content = el;
      if (typeof this.props.contentRef === 'function') {
        this.props.contentRef(el);
      }
    };
    this.state = {
      transitioning: false,
      open: (_props$open = props.open) !== null && _props$open !== void 0 ? _props$open : false,
      windowHeight: 99999
    };
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props);
    window.addEventListener('resize', this.updateHeight);
  }
  componentDidUpdate(prevProps) {
    var _this$props$makeStyle2, _this$props2;
    if (this.props.open !== prevProps.open) {
      this.setState({
        transitioning: true,
        open: !!this.props.open
      });
    }
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2);
  }
  componentWillUnmount() {
    window.removeEventListener('resize', this.updateHeight);
  }
  get defaultFocusElement() {
    return this.props.defaultFocusElement;
  }
  get DOMNode() {
    return this._DOMNode;
  }
  set DOMNode(el) {
    this._DOMNode = el;
  }
  get maskPlacement() {
    if (this.props.overflow === 'fit') {
      return 'stretch';
    } else {
      return 'center';
    }
  }
  renderChildren() {
    const _this$props3 = this.props,
      children = _this$props3.children,
      variant = _this$props3.variant,
      overflow = _this$props3.overflow,
      styles = _this$props3.styles;
    const childrenArray = Children.toArray(children);
    const headerAndBody = [];
    const others = [];
    childrenArray.forEach(child => {
      if (/*#__PURE__*/isValidElement(child)) {
        if (child.type === Modal.Header || child.type === Modal.Body) {
          headerAndBody.push(this.cloneChildWithProps(child, variant, overflow));
        } else {
          others.push(this.cloneChildWithProps(child, variant, overflow));
        }
      }
    });

    // Putting ModalHeader and ModalBody into the same container, so they can be styled together;
    // this is needed to apply a media query breakpoint
    const wrappedHeaderAndBody = headerAndBody.length > 0 ? _jsx("div", {
      css: styles === null || styles === void 0 ? void 0 : styles.joinedHeaderAndBody,
      children: headerAndBody
    }, "header-and-body") : null;
    return [...(wrappedHeaderAndBody ? [wrappedHeaderAndBody] : []), ...others];
  }
  cloneChildWithProps(child, variant, overflow) {
    if (/*#__PURE__*/isValidElement(child)) {
      var _child$props;
      return safeCloneElement(child, {
        variant: variant,
        overflow: (child === null || child === void 0 ? void 0 : (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.overflow) || overflow
      });
    } else {
      return child;
    }
  }
  renderDialog(props) {
    const _this$props4 = this.props,
      onDismiss = _this$props4.onDismiss,
      label = _this$props4.label,
      shouldCloseOnDocumentClick = _this$props4.shouldCloseOnDocumentClick,
      shouldReturnFocus = _this$props4.shouldReturnFocus,
      liveRegion = _this$props4.liveRegion,
      size = _this$props4.size,
      constrain = _this$props4.constrain,
      as = _this$props4.as,
      styles = _this$props4.styles;
    const isFullScreen = size === 'fullscreen';
    const dialog = _jsx(Dialog, {
      ...passthroughProps(props),
      as: as,
      open: true,
      label: label,
      defaultFocusElement: this.defaultFocusElement,
      shouldCloseOnDocumentClick: shouldCloseOnDocumentClick,
      shouldCloseOnEscape: true,
      shouldContainFocus: true,
      shouldReturnFocus: shouldReturnFocus,
      liveRegion: liveRegion,
      onDismiss: onDismiss,
      css: styles === null || styles === void 0 ? void 0 : styles.modal,
      ref: this.contentRef
      // aria-modal="true" see VO bug https://bugs.webkit.org/show_bug.cgi?id=174667
      ,
      children: this.renderChildren()
    });
    return _jsx(Mask, {
      placement: this.maskPlacement,
      fullscreen: constrain === 'window',
      themeOverride: isFullScreen ? {
        borderRadius: '0em',
        borderWidth: '0em'
      } : {},
      children: dialog
    });
  }
  render() {
    var _this$props$styles;
    const _this$props5 = this.props,
      open = _this$props5.open,
      onOpen = _this$props5.onOpen,
      onClose = _this$props5.onClose,
      mountNode = _this$props5.mountNode,
      insertAt = _this$props5.insertAt,
      transition = _this$props5.transition,
      onEnter = _this$props5.onEnter,
      onEntering = _this$props5.onEntering,
      onEntered = _this$props5.onEntered,
      onExit = _this$props5.onExit,
      onExiting = _this$props5.onExiting,
      onExited = _this$props5.onExited,
      constrain = _this$props5.constrain,
      overflow = _this$props5.overflow,
      passthroughProps = _objectWithoutProperties(_this$props5, _excluded);
    const portalIsOpen = this.state.open || this.state.transitioning;
    return _jsx(Portal, {
      mountNode: mountNode,
      insertAt: insertAt,
      open: portalIsOpen,
      onOpen: this.handlePortalOpen,
      children: _jsx(Transition, {
        in: open,
        transitionOnMount: true,
        type: transition,
        onEnter: onEnter,
        onEntering: onEntering,
        onEntered: createChainedFunction(this.handleTransitionComplete, onEntered, onOpen),
        onExit: onExit,
        onExiting: onExiting,
        onExited: createChainedFunction(this.handleTransitionComplete, onExited, onClose),
        children: constrain === 'parent' ? _jsx("span", {
          css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.constrainContext,
          children: this.renderDialog(passthroughProps)
        }) : this.renderDialog(passthroughProps)
      })
    });
  }
}, _Modal.displayName = "Modal", _Modal.componentId = 'Modal', _Modal.propTypes = propTypes, _Modal.allowedProps = allowedProps, _Modal.defaultProps = {
  open: false,
  size: 'auto',
  variant: 'default',
  transition: 'fade',
  insertAt: 'bottom',
  shouldCloseOnDocumentClick: true,
  shouldReturnFocus: true,
  constrain: 'window',
  overflow: 'scroll'
}, _Modal.Header = ModalHeader, _Modal.Body = ModalBody, _Modal.Footer = ModalFooter, _Modal)) || _class) || _class);
export default Modal;
export { Modal, ModalHeader, ModalBody, ModalFooter };